/**
 * Telegram One-Shot Link API Schema
 *
 * Request and response for issuing a one-shot Telegram deep link (Task 8, see
 * apps/edge-api/src/routes/integrations/telegram-link.ts). The link itself
 * does not bind anything — the user presses Start in the chat it opens, and
 * the webhook (Task 9) learns the chat id from Telegram and resolves it back
 * to the nonce's target.
 *
 * SINGLE SOURCE OF TRUTH for this API contract.
 */

import { z } from 'zod/v4';

/**
 * POST /api/v1/integrations/telegram/link request body
 *
 * - `{}` or `{ scope: 'user' }` - a personal link bound to the caller
 * - `{ scope: 'project', projectId }` - a link for a project's channel
 *
 * A `projectId` outside project scope is rejected rather than ignored: a
 * client that sends one meant a project link, and silently handing it a
 * personal one would bind the wrong chat.
 */
export const telegramLinkRequestSchema = z.union([
  z.object({
    scope: z.literal('project'),
    // Rejected, not trimmed: an id with whitespace matches no project as sent.
    projectId: z.string().min(1).max(255).regex(/^\S+$/),
  }),
  z.object({
    scope: z.literal('user').optional(),
    projectId: z.never().optional(),
  }),
]);

export type TelegramLinkRequest = z.infer<typeof telegramLinkRequestSchema>;

/**
 * POST /api/v1/integrations/telegram/link response
 *
 * @property url - t.me deep link to open (opens a DM with the bot, or a
 *   group-add prompt for a project-scoped link)
 * @property expiresAt - ISO 8601 timestamp; the nonce is single-use and
 *   expires after 15 minutes, whichever comes first
 */
export const telegramLinkResponseSchema = z.object({
  url: z.string(),
  expiresAt: z.string(),
});

export type TelegramLinkResponse = z.infer<typeof telegramLinkResponseSchema>;
